import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import { useRuleConstraints } from "hooks/useRuleConstraints";

export default function RuleCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const companyId = useEffectiveCompanyId();
  const { constraints: constraintOptions, loading: loadingConstraints } =
    useRuleConstraints();

  // Scope state
  const [scopeType, setScopeType] = useState("BIN");
  const [scopeValue, setScopeValue] = useState("");

  // Constraints state
  const [constraints, setConstraints] = useState([]);

  // Routing config state
  const [routing, setRouting] = useState([{ mid: "", allocation: "" }]);

  const [saving, setSaving] = useState(false);

  function handleCancel() {
    navigate("/rules");
  }

  // --- Routing handlers
  function handleRoutingChange(index, field, value) {
    const updated = [...routing];
    updated[index][field] = value;
    setRouting(updated);
  }

  function addRoutingRow() {
    setRouting([...routing, { mid: "", allocation: "" }]);
  }

  function removeRoutingRow(index) {
    setRouting(routing.filter((_, i) => i !== index));
  }

  // --- Constraint handlers
  function handleConstraintChange(index, field, value) {
    const updated = [...constraints];
    updated[index][field] = value;
    setConstraints(updated);
  }

  function addConstraintRow() {
    setConstraints([...constraints, { type: "CAMPAIGN", value: "" }]);
  }

  function removeConstraintRow(index) {
    setConstraints(constraints.filter((_, i) => i !== index));
  }

  // --- Save
  async function handleSave() {
    setSaving(true);

    try {
      const payload = {
        scope: { type: scopeType, value: scopeValue },
        constraints: constraints.length > 0 ? constraints : undefined,
        routing: routing.map((r) => ({
          mid: r.mid,
          allocation: Number(r.allocation),
        })),
        status: "Live",
      };

      const res = await makeApiRequest(
        "post",
        apiRoutes.createRule,
        { id: user.id, accessToken: user.accessToken },
        companyId,
        payload
      );

      toast({
        status: "success",
        title: "Rule created",
        description: "Your routing rule is now live.",
        action: {
          label: "View rule",
          onClick: () => navigate(`/rules/${res.data.ruleId}`),
        },
      });

      navigate(`/rules/${res.data.ruleId}`);
    } catch (err) {
      console.error("[RuleCreate] Failed to save rule", err);

      if (err.response?.status === 409) {
        const { existingRuleId } = err.response.data;
        toast({
          status: "warning",
          title: "Rule already exists",
          description: "A rule for this scope already exists.",
          action: {
            label: "View rule",
            onClick: () => navigate(`/rules/${existingRuleId}`),
          },
          duration: 8000,
        });
      } else {
        toast({
          status: "destructive",
          title: "Failed to create rule",
          description: err.response?.data?.error || "Unknown error",
        });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Create Rule"
        subtitle="Define and activate a new payment routing rule."
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={handleCancel}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Rule"}
            </button>
          </div>
        }
      />

      <div className="container py-6 space-y-6">
        {/* Scope */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Scope</h2>
          <p className="text-sm text-muted-foreground">
            Define what this rule applies to.
          </p>
          <select
            className="select select-bordered w-full"
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value)}
          >
            <option value="BIN">BIN</option>
          </select>
          <input
            type="text"
            className="input input-bordered w-full"
            placeholder="e.g. 440066"
            value={scopeValue}
            onChange={(e) => setScopeValue(e.target.value)}
          />
        </section>

        {/* Constraints */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Constraints (Optional)</h2>
          <p className="text-sm text-muted-foreground">
            Add optional constraints (e.g. Campaign) to narrow rule application.
          </p>

          {constraints.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select
                className="select select-bordered w-40"
                value={row.type}
                onChange={(e) =>
                  handleConstraintChange(i, "type", e.target.value)
                }
              >
                <option value="CAMPAIGN">Campaign</option>
                <option value="CATEGORY">Category</option>
                <option value="COUNTRY">Country</option>
              </select>

              {loadingConstraints && row.type !== "COUNTRY" ? (
                <span className="text-sm text-muted-foreground">
                  Loading...
                </span>
              ) : row.type === "COUNTRY" ? (
                <input
                  type="text"
                  className="input input-bordered flex-1"
                  placeholder="e.g. US, GB, DE"
                  value={row.value}
                  onChange={(e) =>
                    handleConstraintChange(i, "value", e.target.value)
                  }
                />
              ) : (
                <select
                  className="select select-bordered flex-1"
                  value={row.value}
                  onChange={(e) =>
                    handleConstraintChange(i, "value", e.target.value)
                  }
                >
                  <option value="">Select constraint...</option>
                  {constraintOptions
                    .filter((c) => c.type.toUpperCase() === row.type)
                    .map((c) => (
                      <option key={c._id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </select>
              )}

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => removeConstraintRow(i)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-2"
            onClick={addConstraintRow}
          >
            <Plus className="h-4 w-4" />
            Add Constraint
          </button>
        </section>

        {/* Routing */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Routing Configuration</h2>
          <p className="text-sm text-muted-foreground">
            Select one or more MIDs and define allocation percentages.
          </p>

          {routing.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                className="input input-bordered flex-1"
                placeholder="MID"
                value={row.mid}
                onChange={(e) => handleRoutingChange(i, "mid", e.target.value)}
              />
              <input
                type="number"
                className="input input-bordered w-28"
                placeholder="%"
                value={row.allocation}
                onChange={(e) =>
                  handleRoutingChange(i, "allocation", e.target.value)
                }
              />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => removeRoutingRow(i)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-2"
            onClick={addRoutingRow}
          >
            <Plus className="h-4 w-4" />
            Add MID
          </button>
        </section>
      </div>
    </div>
  );
}
